import { CallDto, UserDto } from '@act/shared/data-transfer-objects';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { Menu, MenuItem } from '@mui/material';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import useAssignPhoneCallDialog from 'react-components/form-dialog/hooks/use-assign-phone-call-dialog';
import useAssignPhoneNumberDialog from 'react-components/form-dialog/hooks/use-assign-phone-number-dialog';
import useAssignCallAuditorDialog from 'react-components/form-dialog/hooks/use-assign-call-auditor-dialog';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { selectMember } from 'redux/slices/selectedMemberSlice';
import { updateCallReview } from 'redux/slices/callsSlice';
import { callsRequests } from 'redux/requests/calls.request';
import { makeRequest } from 'redux/utils/api-request';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { Permission } from '@act/shared/authorization';
import { useAppDispatch } from 'redux/store-hooks';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { useContext, useEffect } from 'react';
import PostCallSummaryContext from 'react-components/member-page/post-call-summary-drawer/PostCallSummaryContext';

interface CallsActionMenuProps {
  anchorEl?: Element;
  open: boolean;
  onClose: () => void;
  call: CallDto;
  actingUser: UserDto;
}

const CallsActionMenu = ({
  anchorEl,
  open,
  onClose,
  call,
  actingUser,
}: CallsActionMenuProps) => {
  const dispatch = useAppDispatch();
  const openAssignPhoneCallDialog = useAssignPhoneCallDialog();
  const openAssignPhoneNumberDialog = useAssignPhoneNumberDialog();
  const openAssignCallAuditorDialog = useAssignCallAuditorDialog();
  const openConfirmReturnCallDialog = useConfirmationAlertDialog();
  const interactionsDialogHelper = useInteractionsDialog();
  const { setVoiceCallId } = useContext(PostCallSummaryContext);

  // Permission checks
  const callDataQualityFF = useFeatureFlag('call-data-quality');
  const canAssignCallReview = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ASSIGN_CALL_REVIEWS
  );

  const user = membersAppSelectors.useSelectMemberById(
    call?.incoming ? call?.originator?.userId : call?.receiver?.userId
  );
  useEffect(() => {
    if (user) {
      dispatch(selectMember(user?.id));
    }
  }, [user]);
  const externalPhoneNumber = call?.incoming
    ? call?.originator?.phoneNumber
    : call?.receiver?.phoneNumber;
  const isUnknownIncomingCaller = call?.incoming && !call?.originator?.userId;
  const isIncomingFeedbackCall = call?.incoming && call?.isFeedbackCall;
  const canCallBackFromMenu = isUnknownIncomingCaller || isIncomingFeedbackCall;

  const reviewTask = call?.voiceCallAudits?.find(
    task =>
      Number(task.callId) === call?.id &&
      task.assigneeId === actingUser.id &&
      !task.actionTaken &&
      !task.completedAt
  );

  const createReturnCall = async (callToReturn: CallDto, subject: UserDto) => {
    const phoneNumber = callToReturn?.incoming
      ? callToReturn?.originator?.phoneNumber
      : callToReturn?.receiver?.phoneNumber;
    const isUnknown = callToReturn?.incoming
      ? !callToReturn?.originator?.userId
      : !callToReturn?.receiver?.userId;

    let confirmationTitle: string;
    let confirmationMessage: string;
    if (isUnknown) {
      confirmationTitle = `Return unknown${
        callToReturn.isFeedbackCall ? ' feedback' : ''
      } call`;
      confirmationMessage = `Do you want to return a phone call to ${phoneNumber}?`;
    } else {
      confirmationTitle = `Return${
        callToReturn.isFeedbackCall ? ' feedback' : ''
      } call to ${subject?.name}`;
      confirmationMessage = `Do you want to return a phone call to ${phoneNumber}?`;
    }

    const shouldReturnCall = await openConfirmReturnCallDialog({
      title: confirmationTitle,
      message: confirmationMessage,
      icon: <LocalPhoneIcon />,
    });
    if (shouldReturnCall) {
      if (isUnknown) {
        makeRequest(callsRequests.createCall, {
          params: null,
          body: {
            targetUserId: subject?.id,
            phoneNumber,
          },
        });
      } else {
        const returnedCall = await makeRequest(callsRequests.returnCall, {
          params: null,
          body: {
            callId: callToReturn.id,
          },
        });
        interactionsDialogHelper.editCall({
          user: subject,
          call: returnedCall,
        });
      }
    }
  };
  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      <MenuItem
        onClick={async () => {
          void openAssignPhoneCallDialog({
            call: call,
          });
          onClose();
        }}
      >
        Assign call (one time)
      </MenuItem>
      <MenuItem
        onClick={async () => {
          void openAssignPhoneNumberDialog({
            phoneNumber: externalPhoneNumber,
            callId: call.id,
          });
          onClose();
        }}
      >
        Assign phone number
      </MenuItem>
      {canCallBackFromMenu && (
        <MenuItem
          onClick={async () => {
            onClose();
            createReturnCall(call, user);
          }}
        >
          Call back
        </MenuItem>
      )}
      {canAssignCallReview && (
        <MenuItem
          onClick={() => {
            void openAssignCallAuditorDialog({
              call: call,
              callsReviewTasks: call?.voiceCallAudits,
            });
            onClose();
          }}
        >
          Share call
        </MenuItem>
      )}
      {/* Show todo-related menu items based on existing todos */}
      {reviewTask && (
        <MenuItem
          onClick={async () => {
            // Find the specific review task for this call and user
            if (reviewTask) {
              await dispatch(
                updateCallReview({
                  params: { auditId: reviewTask.id },
                  body: { actionTaken: false },
                })
              );
            }
            onClose();
          }}
        >
          No action required
        </MenuItem>
      )}
      {callDataQualityFF && (
        <MenuItem
          onClick={() => {
            setVoiceCallId(call.id);
            onClose();
          }}
        >
          View summary
        </MenuItem>
      )}
    </Menu>
  );
};

export default CallsActionMenu;
